import React, { useEffect, useState } from "react";
import styles from "./AddJobLogs.module.scss";
import AdminHeader from "../../../Components/AdminHeader/AdminHeader";
import { getJobList } from "../../../Redux/Stats/StatsActions";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../Redux/store";
import InlineLoader from "../../../Components/InlineLoader/InlineLoader";
import { AgGridReact } from "ag-grid-react";
import { ColDef, GridReadyEvent, RowClickedEvent } from "ag-grid-community";
import AddJobLogsModal from "../../../Components/AddJobLogsModal/AddJobLogsModal";
import { DateTime } from "luxon";

export interface AddJobLogsType {
  id?: number;
  number: string;
  name: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  county: string;
  jobType: string;
  jobSubtype: string;
  emailService?: string;
  location: string;
  buildingType: string;
  team: string;
  referralSource: string;
  referralCompany: string;
  lossDescription: string;
  rm: string;
  event: string;
  contact_email: string;
  contact_phone: string;
  claimNumber: string;
  lossDate: string;
  claimDate: string;
  receivedDate: string;
  status: string;
  provenJobId: number;
  provenJobResponse: {
    ID: number;
    Status: string;
    "xmlns:a": string;
    "xmlns:i": string;
    RemoteID: string;
    ProcessedTime: string;
    AdditionalInfo: {
      "i:nil": string;
    };
    AdditionalKeys: {
      "i:nil": string;
      "xmlns:b": string;
    };
  };

  messageId: string;
  createdAt: string;
  updatedAt: string;
}

const columnDefs: ColDef<AddJobLogsType>[] = [
  {
    headerName: "Number",
    field: "number",
    flex: 1,
    minWidth: 100,
    maxWidth: 200,
  },
  {
    headerName: "Proven Job Id",
    field: "provenJobId",
    flex: 1,
    maxWidth: 150,
  },
  {
    headerName: "Name",
    field: "name",
    maxWidth: 150,
  },

  {
    headerName: "Email",
    field: "contact_email",
    flex: 1,
    minWidth: 150,
    maxWidth: 250,
  },

  {
    headerName: "Created At",
    field: "createdAt",
    flex: 1,
    maxWidth: 200,
    minWidth: 150,
    valueGetter: (params) => {
      return params?.data?.createdAt
        ? DateTime.fromISO(params?.data?.createdAt, {
            zone: "America/New_York",
          }).toFormat("MM/dd/yyyy hh:mm a")
        : "";
    },
  },

  {
    headerName: "Phone",
    field: "contact_phone",
    maxWidth: 150,
  },

  {
    headerName: "Event",
    field: "event",
    maxWidth: 150,
  },

  {
    headerName: "Building Type",
    field: "buildingType",
    maxWidth: 150,
  },

  {
    headerName: "Address",
    field: "address",
    flex: 1,
    minWidth: 250,
    valueGetter: (params) => {
      return `${params?.data?.address}, ${params?.data?.city}, ${params?.data?.state}, ${params?.data?.zipCode}`;
    },
    filter: false,
    sortable: false,
    cellStyle: {
      whiteSpace: "normal",
      wordWrap: "break-word",
      overflowWrap: "break-word",
      lineHeight: "1.5",
      padding: "10px",
      textAlign: "left",
    },
  },
];

const defaultColDef: ColDef = {
  resizable: true,
  sortable: true,
  filter: false,
  suppressPaste: true,
  suppressNavigable: true,
  suppressMovable: true,
  wrapText: true,
  autoHeight: true,
};

function AddJobLogs() {
  const dispatch = useDispatch<AppDispatch>();
  const { job_list_loading, job_list_data } = useSelector(
    (state: RootState) => state.stats
  );
  const [search, setSearch] = useState("");
  const [rowData, setRowData] = useState<AddJobLogsType[]>([]);
  const [gridApi, setGridApi] = useState<any>(null);
  const [selectedRowData, setSelectedRowData] = useState<AddJobLogsType | null>(
    null
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  const onRowClicked = (event: RowClickedEvent<AddJobLogsType>) => {
    if (event?.data) {
      setSelectedRowData(event.data);
      setIsModalOpen(true);
    }
  };

  const filteredData = rowData?.filter((item: AddJobLogsType) => {
    const query = search?.toLowerCase() || "";
    return (
      item?.name?.toLowerCase()?.includes(query) ||
      item?.number?.toLowerCase()?.includes(query) ||
      item?.claimNumber?.toLowerCase()?.includes(query) ||
      item?.address?.toLowerCase()?.includes(query) ||
      item?.city?.toLowerCase()?.includes(query) ||
      item?.state?.toLowerCase()?.includes(query) ||
      item?.zipCode?.toLowerCase()?.includes(query) ||
      item?.provenJobId?.toString()?.includes(query) ||
      item?.contact_email?.toLowerCase()?.includes(query) ||
      item?.contact_phone?.toLowerCase()?.includes(query) ||
      item?.event?.toLowerCase()?.includes(query) ||
      item?.buildingType?.toLowerCase()?.includes(query) ||
      item?.messageId?.toLowerCase()?.includes(query)
    );
  });

  const refreshHandler = () => {
    if (!job_list_loading) {
      dispatch(getJobList());
    }
  };

  const onGridReady = (params: GridReadyEvent) => {
    setGridApi(params.api);
    params.api.sizeColumnsToFit();
  };

  useEffect(() => {
    dispatch(getJobList());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!gridApi) return;

    const handleResize = () => {
      gridApi.sizeColumnsToFit();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [gridApi]);

  useEffect(() => {
    if (job_list_data) {
      setRowData(job_list_data);
    }
  }, [job_list_data]);

  return (
    <React.Fragment>
      <AddJobLogsModal
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        selectedRowData={selectedRowData}
      />

      <div className={styles.mainContainer}>
        <AdminHeader />
        <div className={styles.bodyContainer}>
          <div className={styles.headingContainer}>
            <h1 className={styles.heading}>Add Job Logs</h1>
          </div>
          <form className={styles.searchContainer}>
            <div className={styles.leftContainer}>
              <div className={styles.inputContainer}>
                <input
                  type="text"
                  placeholder="Search"
                  className={styles.input}
                  value={search}
                  onChange={(e) => {
                    e.preventDefault();
                    setSearch(e.target.value)}}
                />
              </div>
            </div>
            <div className={styles.rightContainer}>
              <div className={styles.buttonContainer}>
                <button
                  type="button"
                  className={styles.button}
                  onClick={refreshHandler}
                >
                  Refresh
                </button>
              </div>
            </div>
          </form>
          {job_list_loading && (
            <div className={styles.loadingContainer}>
              <InlineLoader color="#000000" />
            </div>
          )}

          {Array.isArray(job_list_data) && job_list_data.length === 0 && (
            <div className={styles.noLogsContainer}>
              <p className={styles.noLogsText}>No logs found</p>
            </div>
          )}

          <div className={styles.gridContainer}>
            <AgGridReact
              className={styles.grid}
              rowData={filteredData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              onGridReady={onGridReady}
              pagination={false}
              animateRows={true}
              domLayout="autoHeight"
              onRowClicked={onRowClicked}
            />
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}

export default AddJobLogs;
